import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Leaf, LayoutDashboard, Smile, Calendar, BarChart3, User, LogOut, Image, Crown, FileText, Moon, Sun, Shield } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { API, fetchWithAuth } from "@/lib/api";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Početna" },
  { to: "/mood", icon: Smile, label: "Novo Raspoloženje" },
  { to: "/calendar", icon: Calendar, label: "Kalendar" },
  { to: "/statistics", icon: BarChart3, label: "Statistika" },
  { to: "/weekly-report", icon: FileText, label: "Nedeljni Izveštaj" },
  { to: "/share", icon: Image, label: "Podeli" },
  { to: "/premium", icon: Crown, label: "Premium" },
  { to: "/profile", icon: User, label: "Profil" },
];

const mobileNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Početna" },
  { to: "/mood", icon: Smile, label: "Novo" },
  { to: "/calendar", icon: Calendar, label: "Kalendar" },
  { to: "/share", icon: Image, label: "Podeli" },
  { to: "/profile", icon: User, label: "Profil" },
];

export default function AppLayout({ children, user }) {
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await fetchWithAuth(`${API}/admin/check`);
        if (res.ok) {
          const data = await res.json();
          setIsAdmin(data.is_admin);
        }
      } catch {}
    };
    checkAdmin();
  }, []);

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] dark:bg-[#1a2023] flex transition-colors duration-300">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white dark:bg-[#1e2729] border-r border-[#EBEBE8] dark:border-[#2a3538] p-6 transition-colors duration-300">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-9 h-9 bg-[#4A6C6F] rounded-full flex items-center justify-center">
            <Leaf className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-lg text-[#2D3A3A] dark:text-[#E8EAE8] font-light">umiri.me</span>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-[#D6E0D6] dark:bg-[#2a3a3a] text-[#2D3A3A] dark:text-[#E8EAE8] font-medium"
                    : "text-[#5C6B6B] dark:text-[#8A9999] hover:bg-[#F2F4F0] dark:hover:bg-[#243030]"
                }`
              }
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-all duration-200 mt-4 border-t border-[#EBEBE8] dark:border-[#2a3538] pt-4 ${
                  isActive
                    ? "bg-[#E8D4C8] dark:bg-[#3a2a2a] text-[#8B5A2B] dark:text-[#E09F7D] font-medium"
                    : "text-[#8B5A2B] dark:text-[#B8A07C] hover:bg-[#F5EBE4] dark:hover:bg-[#302525]"
                }`
              }
            >
              <Shield className="w-5 h-5" strokeWidth={1.5} />
              Admin Panel
            </NavLink>
          )}
        </nav>

        {/* User info */}
        <div className="mt-auto pt-6 border-t border-[#EBEBE8] dark:border-[#2a3538]">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
            ) : (
              <div className="w-9 h-9 bg-[#D6E0D6] dark:bg-[#2a3a3a] rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-[#4A6C6F]" />
              </div>
            )}
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-[#2D3A3A] dark:text-[#E8EAE8] truncate">{user?.name}</p>
              <p className="text-xs text-[#8A9999] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-[#8A9999] hover:text-[#D66A6A] transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Odjavi se
          </button>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            className="flex items-center gap-2 text-sm text-[#8A9999] hover:text-[#4A6C6F] transition-colors mt-2"
          >
            {isDark ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            {isDark ? "Svetla tema" : "Tamna tema"}
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-[#1e2729] border-t border-[#EBEBE8] dark:border-[#2a3538] z-50 flex justify-around py-2 px-1 transition-colors duration-300">
        {mobileNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`mobile-nav-${item.to.slice(1)}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl text-xs transition-all ${
                isActive ? "text-[#4A6C6F] font-medium" : "text-[#8A9999]"
              }`
            }
          >
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px]">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Main content */}
      <main className="flex-1 pb-20 md:pb-0 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-12">
          {children}
        </div>
      </main>
    </div>
  );
}
